import React, { useState, useEffect, useRef } from 'react';

function RouletteWheel({ numbers, result, isSpinning }) {
  const [rotation, setRotation] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const lastProcessedResult = useRef(null);
  const currentRotation = useRef(0); // Track accumulated rotation

  // Calculate the angle for each number slot (38 slots for American roulette)
  const slotAngle = 360 / 38;

  // When a new result comes in and we're spinning, animate to that result
  useEffect(() => {
    // Only process if we have a new result that we haven't animated to yet
    if (!result || !isSpinning) {
      return;
    }

    // Check if we've already processed this exact result object
    if (lastProcessedResult.current === result) {
      return;
    }

    lastProcessedResult.current = result;
    setIsAnimating(true);

    // Find the index of the result in the numbers array
    const resultIndex = numbers.findIndex(n => n.num === result.num);

    if (resultIndex === -1) return;

    // CLOCKWISE ROTATION APPROACH (ACCUMULATING):
    // Each slot's center is at: (index * slotAngle + slotAngle/2)
    // Paths are drawn with -90° offset, so slot centers in SVG coords are at: slotCenter - 90
    // The pointer is at the top (-90° in SVG coords, or 270° normalized)

    const slotCenterLogical = resultIndex * slotAngle + slotAngle / 2;
    const slotCenterSVG = slotCenterLogical - 90;

    // Add random offset within the slot for more realistic landing
    // Use deterministic random based on result + timestamp so all users see same position
    const offsetSeed = (typeof result.num === 'number' ? result.num : result.num.toString().charCodeAt(0)) + Math.floor(Date.now() / 30000);
    const randomOffset = ((offsetSeed * 9301 + 49297) % 233) / 233; // Pseudo-random 0-1
    const slotOffset = (randomOffset - 0.5) * slotAngle * 0.6; // Random offset within 60% of slot width

    // Where we currently are (normalized to 0-360)
    const currentNormalized = ((currentRotation.current % 360) + 360) % 360;

    // Where the target slot currently is after applying our current rotation
    const currentSlotPosition = ((slotCenterSVG + currentNormalized) % 360 + 360) % 360;

    // We want the slot to end up at 270° (-90°, the pointer position), plus our random offset
    const targetPosition = 270 - slotOffset; // Subtract offset because we're rotating the wheel

    // How much clockwise rotation from current slot position to target?
    let rotationNeeded = targetPosition - currentSlotPosition;

    // Make sure we go the full clockwise way (positive rotation)
    if (rotationNeeded < 0) rotationNeeded += 360;

    // Add deterministic full spins based on result (same for all users)
    const seed = typeof result.num === 'number' ? result.num : result.num.toString().charCodeAt(0);
    const fullSpins = 5 + (seed % 3);

    // Total rotation to ADD to current rotation (positive = clockwise)
    const additionalRotation = rotationNeeded + (fullSpins * 360);
    const targetAngle = currentRotation.current + additionalRotation;

    // Update ref and state
    currentRotation.current = targetAngle;
    setRotation(targetAngle);

    // Stop animating after spin completes (6 seconds to match CSS transition)
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, 6000);

    return () => clearTimeout(timer);
  }, [result, isSpinning, numbers, slotAngle]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      padding: '6px',
      boxSizing: 'border-box'
    }}>
      {/* Wheel column: pointer + wheel */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, width: '100%', minHeight: 0 }}>
        {/* Pointer at top */}
        <div style={{
          width: 0,
          height: 0,
          borderLeft: '12px solid transparent',
          borderRight: '12px solid transparent',
          borderTop: '20px solid #FFD700',
          marginBottom: '-4px',
          zIndex: 10,
          flexShrink: 0,
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
        }} />

      {/* Wheel container — square, fills available height */}
      <div style={{
        position: 'relative',
        aspectRatio: '1 / 1',
        height: '100%',
        maxHeight: '100%',
        borderRadius: '50%',
        background: 'linear-gradient(145deg, #2a2a2a, #1a1a1a)',
        boxShadow: '0 10px 50px rgba(0,0,0,0.7), inset 0 0 30px rgba(0,0,0,0.5)',
        border: '6px solid #FFD700',
        overflow: 'hidden'
      }}>
        {/* Rotating wheel */}
        <svg
          viewBox="0 0 400 400"
          style={{
            width: '100%',
            height: '100%',
            transform: `rotate(${rotation}deg)`,
            transformOrigin: 'center center',
            transition: isAnimating ? 'transform 6s cubic-bezier(0.33, 1, 0.68, 1)' : 'none'
          }}
        >
          {/* Draw each number slot */}
          {numbers.map((num, index) => {
            const angle = index * slotAngle;
            const nextAngle = (index + 1) * slotAngle;

            const startAngle = (angle - 90) * (Math.PI / 180);
            const endAngle = (nextAngle - 90) * (Math.PI / 180);

            const x1 = 200 + 190 * Math.cos(startAngle);
            const y1 = 200 + 190 * Math.sin(startAngle);
            const x2 = 200 + 190 * Math.cos(endAngle);
            const y2 = 200 + 190 * Math.sin(endAngle);

            const pathData = `M 200 200 L ${x1} ${y1} A 190 190 0 0 1 ${x2} ${y2} Z`;

            const fillColor = num.color === 'red' ? '#DC143C' :
                            num.color === 'black' ? '#1a1a1a' : '#228B22';

            const textAngle = (angle + slotAngle / 2 - 90) * (Math.PI / 180);
            const textX = 200 + 145 * Math.cos(textAngle);
            const textY = 200 + 145 * Math.sin(textAngle);
            const textRotation = angle + slotAngle / 2;

            return (
              <g key={index}>
                <path d={pathData} fill={fillColor} stroke="#FFD700" strokeWidth="1" />
                <text
                  x={textX}
                  y={textY}
                  fill="white"
                  fontSize="16"
                  fontWeight="bold"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  transform={`rotate(${textRotation}, ${textX}, ${textY})`}
                  style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}
                >
                  {num.num}
                </text>
              </g>
            );
          })}

          {/* Center circle */}
          <circle cx="200" cy="200" r="40" fill="#FFD700" />
          <circle cx="200" cy="200" r="35" fill="#1a1a1a" />
        </svg>
      </div>
      </div>{/* end wheel column */}
    </div>
  );
}

export default RouletteWheel;
